import {makeAutoObservable, reaction} from "mobx";
import {api, catchApi} from "../../api";
import {
    AcquisitionDataDto,
    AcquisitionDocumentDto,
    AcquisitionDocumentStatusDto,
    AcquisitionDto,
    AcquisitionStatusDto
} from "../../api/sca";
import {equals} from "../../utils";
import {validationStore} from "../ValidationStore";
import {ScaFormData, ScaFormValidation} from "./commons";
import {buildScaCustomerItem, ScaCustomerItem} from "./ScaCustomerStore";
import {FmsPrinterItem} from "../fms";
import {userPrinterPreferenceStore} from "../ums";
import {scaZebraPrinterStore} from "./ScaPrinterStore";

const systemApi = api.sca.api

class ScaDetailStore {
    get printer(): FmsPrinterItem | undefined {
        return this._printer;
    }

    set printer(value: FmsPrinterItem | undefined) {
        this._printer = value;
    }
    get customer(): ScaCustomerItem | undefined {
        return this._customer;
    }

    private set customer(value: ScaCustomerItem | undefined) {
        this._customer = value;
    }
    get id(): string | undefined {
        return this._id;
    }

    set id(value: string | undefined) {
        this._id = value;
    }
    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }
    get saving(): boolean {
        return this._saving;
    }

    set saving(value: boolean) {
        this._saving = value;
    }
    get vin(): string {
        return this._vin;
    }

    set vin(value: string) {
        this._vin = value;
    }
    get externalReference(): string {
        return this._externalReference;
    }

    set externalReference(value: string) {
        this._externalReference = value;
    }
    get documents(): AcquisitionDocumentDto[] {
        return this._documents;
    }

    set documents(value: AcquisitionDocumentDto[]) {
        this._documents = value;
    }
    get customerId(): string {
        return this._customerId;
    }

    set customerId(value: string) {
        this._customerId = value;
    }
    get correlationId(): string {
        return this._correlationId;
    }

    set correlationId(value: string) {
        this._correlationId = value;
    }
    get loaded(): AcquisitionDto | undefined {
        return this._loaded;
    }

    private set loaded(value: AcquisitionDto | undefined) {
        this._loaded = value;
    }

    get dirty(): boolean {
        return !equals(this.buildDto(), this.loaded?.data)
    }

    get formData(): ScaFormData {
        const {
            loaded,
            customer,
            vin,
            externalReference,
            documents
        } = this
        return {
            vin,
            externalReference,
            customer,
            documents: documents.map((d, index) => {
                const dataElementId = d.dataElementId || undefined
                const documentId = d.documentId || undefined
                const securityCode = d.securityCode || undefined
                const serialNumber = d.serialNumber || undefined
                const status = securityCode ? (
                    d.status === AcquisitionDocumentStatusDto.CODE_INVALID ? d.status : AcquisitionDocumentStatusDto.READ
                ) : (
                    d.status === AcquisitionDocumentStatusDto.READ ? AcquisitionDocumentStatusDto.INITIALIZED : d.status
                )
                const sourceStatus = (loaded?.data.documents || [])[index].status
                return {...d, securityCode, status, dataElementId, documentId, serialNumber, sourceStatus}
            }),
        }
    }

    set formData(v: ScaFormData) {
        const {
            customer,
            vin,
            externalReference,
            documents,
        } = v
        this.customer = customer
        this.customerId = customer?.id || ''
        this.vin = vin
        this.externalReference = externalReference
        this.documents = documents
    }

    get formValidation(): ScaFormValidation {
        const securityCodes = this.documents.map((d, index) => validationStore.getFor(`documents[${index}].securityCode`))
        return {
            securityCodes
        }
    }

    get disabled(): boolean {
        const {documents, loaded} = this
        if (!loaded || loaded.data.status !== AcquisitionStatusDto.ACQUISITION_READY) {
            return true
        }
        const foundWithoutDocumentId = documents.findIndex(e => !e.documentId)

        return foundWithoutDocumentId !== -1
    }

    get closeableWithError(): boolean {
        const {documents, loaded} = this
        if (!loaded || loaded.data.status !== AcquisitionStatusDto.ACQUISITION_READY) {
            return false
        }
        const foundWithoutDocumentId = documents.findIndex(e => !e.documentId)
        return foundWithoutDocumentId !== -1
    }

    get closeable(): boolean {
        if (!this.dirty) {
            return false
        }
        if (!this.valid) {
            return false
        }

        const existingDocumentWithInitializedState= this.documents.filter(e => e.documentId)
            .findIndex(e => e.status === AcquisitionDocumentStatusDto.INITIALIZED || (
                e.status === AcquisitionDocumentStatusDto.READ && !Boolean(e.securityCode)
            ))

        return existingDocumentWithInitializedState === -1
    }

    private _correlationId: string = ''
    private _customerId: string = ''
    private _documents: AcquisitionDocumentDto[] = []
    private _externalReference: string = ''
    private _vin: string = ''
    private _printer: FmsPrinterItem | undefined

    private _id: string | undefined
    private _loaded: AcquisitionDto | undefined

    private _customer: ScaCustomerItem | undefined

    private _saving: boolean = false
    private _valid: boolean = false

    constructor() {
        makeAutoObservable(this)
        reaction(() => [
            userPrinterPreferenceStore.zebraPrinterId,
            scaZebraPrinterStore.options
        ], () => this.resetDefaultPrinter())
    }

    resetDefaultPrinter() {
        const printerId = userPrinterPreferenceStore.zebraPrinterId
        const options = scaZebraPrinterStore.options
        this.printer = options.find(p => p.id === printerId) || undefined
    }

    async reset() {
        const {loaded} = this
        this.valid = false
        if (!loaded) {
            return
        }
        const {data} = loaded

        if (this.customerId !== data.customerId) {
            await this.setupCustomer(data.customerId)
        }

        this.vin = data.vin
        this.documents = data.documents
        this.customerId = data.customerId
        this.correlationId = data.correlationId
        this.externalReference = data.externalReference || ''
    }

    async validate() {
        const dto = this.buildDto()

        await validationStore.validate(systemApi.validateSecurityCodeAcquisition, dto)

        this.valid = Boolean(validationStore.valid)
    }

    async load(): Promise<AcquisitionDto | undefined> {
        const {id} = this
        if (!id) {
            return
        }
        const response = await catchApi(
            async () => await systemApi.getSecurityCodeAcquisition(id)
        )

        const data = response?.data
        this.loaded = data
        await this.reset()
        return data
    }

    async save(): Promise<AcquisitionDto | undefined> {
        const {loaded, id} = this
        if (!loaded || !id) {
            return
        }
        const dto:AcquisitionDto = {
            audit: loaded.audit,
            data: this.buildDto()
        }
        const response = await catchApi(
            async () => await systemApi.updateSecurityCodeAcquisition(id, dto)
        )

        const data = response?.data
        if (data) {
            this.loaded = data
            await this.reset()
        }
        return data
    }

    private buildDto(): AcquisitionDataDto {
        const status = AcquisitionStatusDto.INITIALIZED
        const {
            printer,
            vin,
            documents,
            customerId,
            correlationId,
            externalReference
        } = this

        return {
            vin,
            documents,
            customerId,
            correlationId,
            printerId: printer?.id,
            externalReference: externalReference || undefined,
            status
        }
    }

    private async setupCustomer(customerId: string) {
        const data = (await systemApi.findCustomers()).data
        this.customer = data.map(e => {
            return buildScaCustomerItem(e)
        }).find(e => e.id === customerId)
    }
}

const scaDetailStore = new ScaDetailStore()

export {
    scaDetailStore
}
